import React, { useEffect, useRef, useState } from 'react';
import { KNOWLEDGE, askKnowledgeBase } from '../data/knowledge';
import { track } from '../lib/track';

type Line = { kind: 'cmd' | 'out' | 'err' | 'ok'; text: string };

const kb = (id: string): string =>
  KNOWLEDGE.find(e => e.id === id)?.answer || 'not found';

const FILES = [
  'experience.log', 'skills.md', 'education.md', 'contact.txt', 'projects/', 'certifications/'
];

const PROJECT_FILES = ['keepsake', 'meshyay', 'tijarat360', 'builder-breakers', 'kabutar-ud'];

const HELP = `available commands:
  whoami                    who is mujahid?
  ls [dir]                  list files (try: ls projects/)
  cat <file>                read a file (try: cat experience.log)
  open <page>               go to a page: home|experience|projects|skills|education
  cv                        download resume as PDF
  contact                   how to reach mujahid
  sudo hire-me              (try it)
  clear                     clear the screen
  exit                      close the terminal
…or just type a question in plain english — the resume agent answers.`;

const BANNER = `mujahid-masood terminal v2.0 — type 'help' to get started`;

function runCommand(raw: string): { lines: Line[]; action?: 'clear' | 'exit' | string } {
  const input = raw.trim();
  const [cmd, ...rest] = input.split(/\s+/);
  const arg = rest.join(' ').toLowerCase();

  switch (cmd.toLowerCase()) {
    case 'help':
    case '?':
      return { lines: [{ kind: 'out', text: HELP }] };

    case 'whoami':
      return { lines: [{ kind: 'out', text: kb('whoami') }] };

    case 'ls': {
      if (arg.startsWith('project'))
        return { lines: [{ kind: 'out', text: PROJECT_FILES.join('   ') }] };
      if (arg.startsWith('cert'))
        return { lines: [{ kind: 'out', text: kb('certifications') }] };
      return { lines: [{ kind: 'out', text: FILES.join('   ') }] };
    }

    case 'cat': {
      if (!arg) return { lines: [{ kind: 'err', text: 'cat: missing file. try: cat experience.log' }] };
      if (arg.includes('experience')) return { lines: [{ kind: 'out', text: kb('experience') }] };
      if (arg.includes('skill')) return { lines: [{ kind: 'out', text: kb('skills') }] };
      if (arg.includes('education')) return { lines: [{ kind: 'out', text: kb('education') }] };
      if (arg.includes('contact')) return { lines: [{ kind: 'out', text: kb('contact') }] };
      if (arg.includes('cert')) return { lines: [{ kind: 'out', text: kb('certifications') }] };
      if (arg.includes('keepsake')) return { lines: [{ kind: 'out', text: kb('keepsake') }] };
      if (arg.includes('meshyay')) return { lines: [{ kind: 'out', text: kb('meshyay') }] };
      if (arg.includes('tijarat') || arg.includes('builder') || arg.includes('kabutar'))
        return { lines: [{ kind: 'out', text: kb('products') }] };
      if (arg.includes('project')) return { lines: [{ kind: 'out', text: kb('ai-projects') }] };
      return { lines: [{ kind: 'err', text: `cat: ${arg}: no such file. try 'ls'` }] };
    }

    case 'experience':
      return { lines: [{ kind: 'out', text: kb('experience') }] };
    case 'skills':
      return { lines: [{ kind: 'out', text: kb('skills') }] };
    case 'education':
      return { lines: [{ kind: 'out', text: kb('education') }] };
    case 'projects':
      return { lines: [{ kind: 'out', text: kb('ai-projects') }] };
    case 'certs':
    case 'certifications':
      return { lines: [{ kind: 'out', text: kb('certifications') }] };
    case 'contact':
    case 'email':
      return { lines: [{ kind: 'out', text: kb('contact') }] };

    case 'cv':
    case 'resume':
    case 'download':
      return {
        lines: [{ kind: 'ok', text: 'downloading Mujahid_Masood_Resume.pdf…' }],
        action: 'download-cv'
      };

    case 'open':
    case 'cd': {
      const pages = ['home', 'experience', 'projects', 'skills', 'education'];
      const target = pages.find(p => arg.includes(p));
      if (target) return { lines: [{ kind: 'ok', text: `opening /${target}…` }], action: `/${target}` };
      return { lines: [{ kind: 'err', text: `open: unknown page. pages: ${pages.join(' ')}` }] };
    }

    case 'sudo': {
      if (arg.includes('hire'))
        return {
          lines: [
            { kind: 'ok', text: '[sudo] permission granted.' },
            { kind: 'out', text: 'Excellent decision. Opening a mail draft to mujahid.mms@gmail.com…' }
          ],
          action: 'mailto:mujahid.mms@gmail.com?subject=Let%27s%20talk'
        };
      return { lines: [{ kind: 'err', text: 'visitor is not in the sudoers file. This incident will be reported.' }] };
    }

    case 'clear':
      return { lines: [], action: 'clear' };
    case 'exit':
    case 'quit':
      return { lines: [], action: 'exit' };

    case 'pwd':
      return { lines: [{ kind: 'out', text: '/home/mujahid/resume' }] };
    case 'echo':
      return { lines: [{ kind: 'out', text: rest.join(' ') }] };
    case 'date':
      return { lines: [{ kind: 'out', text: new Date().toString() }] };
    case 'rm':
      return { lines: [{ kind: 'err', text: 'rm: nice try. resume is read-only.' }] };

    default:
      // plain-english question → knowledge base agent
      return { lines: [{ kind: 'out', text: askKnowledgeBase(input) }] };
  }
}

const Terminal: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [lines, setLines] = useState<Line[]>([{ kind: 'ok', text: BANNER }]);
  const [input, setInput] = useState('');
  const [history, setHistory] = useState<string[]>([]);
  const [histIdx, setHistIdx] = useState(-1);
  const logRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (logRef.current) logRef.current.scrollTop = logRef.current.scrollHeight;
  }, [lines, open]);

  // press ` (backtick) anywhere to open the terminal
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const tag = (document.activeElement?.tagName || '').toLowerCase();
      if (e.key === '`' && !open && tag !== 'input' && tag !== 'textarea') {
        e.preventDefault();
        setOpen(true);
      }
      if (e.key === 'Escape' && open) setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  const exec = (raw: string) => {
    const text = raw.trim();
    if (!text) return;
    setHistory(h => [...h, text]);
    setHistIdx(-1);
    const { lines: out, action } = runCommand(text);
    // track the command word only — free-text questions are logged as 'question', never verbatim
    const word = text.split(/\s+/)[0].toLowerCase();
    const known = ['help', '?', 'whoami', 'ls', 'cat', 'experience', 'skills', 'education',
      'projects', 'certs', 'certifications', 'contact', 'email', 'cv', 'resume', 'download',
      'open', 'cd', 'sudo', 'clear', 'exit', 'quit', 'pwd', 'echo', 'date', 'rm'];
    track('terminal_command', { command: known.includes(word) ? word : 'question' });
    if (action === 'clear') {
      setLines([]);
      setInput('');
      return;
    }
    setLines(l => [...l, { kind: 'cmd', text }, ...out]);
    setInput('');
    if (action === 'exit') {
      setTimeout(() => setOpen(false), 250);
    } else if (action === 'download-cv') {
      track('cv_download', { source: 'terminal' });
      setTimeout(() => {
        const a = document.createElement('a');
        a.href = '/Mujahid_Masood_Resume.pdf';
        a.download = 'Mujahid_Masood_Resume.pdf';
        a.click();
      }, 400);
    } else if (action && action.startsWith('/')) {
      setTimeout(() => {
        window.location.href = action;
      }, 500);
    } else if (action && action.startsWith('mailto:')) {
      setTimeout(() => {
        window.location.href = action;
      }, 700);
    }
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === 'Return' || e.keyCode === 13) {
      e.preventDefault();
      exec(input);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (history.length === 0) return;
      const idx = histIdx === -1 ? history.length - 1 : Math.max(0, histIdx - 1);
      setHistIdx(idx);
      setInput(history[idx]);
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (histIdx === -1) return;
      const idx = histIdx + 1;
      if (idx >= history.length) {
        setHistIdx(-1);
        setInput('');
      } else {
        setHistIdx(idx);
        setInput(history[idx]);
      }
    }
  };

  if (!open) {
    return (
      <button
        className="chat-fab"
        onClick={() => {
          track('terminal_opened');
          setOpen(true);
        }}
        aria-label="Open terminal"
      >
        $ open terminal <span className="chat-fab__hint">[`]</span>
      </button>
    );
  }

  return (
    <div className="term-overlay" onClick={() => inputRef.current?.focus()}>
      <div className="term-window">
        <div className="chat-panel__bar">
          <div className="chat-panel__dots">
            <span className="chat-panel__dot chat-panel__dot--r" />
            <span className="chat-panel__dot chat-panel__dot--y" />
            <span className="chat-panel__dot chat-panel__dot--g" />
          </div>
          <span className="chat-panel__title">visitor@mujahidmasood: ~/resume</span>
          <button className="chat-panel__close" onClick={e => { e.stopPropagation(); setOpen(false); }} aria-label="Close terminal">
            ✕
          </button>
        </div>
        <div className="term-log" ref={logRef}>
          {lines.map((l, i) => (
            <div className={`term-line term-line--${l.kind}`} key={i}>
              {l.kind === 'cmd' ? (
                <>
                  <span className="term-ps1">visitor@mujahidmasood:~$</span> {l.text}
                </>
              ) : (
                l.text
              )}
            </div>
          ))}
          <form
            className="term-input-row"
            onSubmit={e => {
              e.preventDefault();
              exec(input);
            }}
          >
            <span className="term-ps1">visitor@mujahidmasood:~$</span>
            <input
              ref={inputRef}
              className="term-input"
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={onKeyDown}
              autoFocus
              autoCapitalize="off"
              autoCorrect="off"
              spellCheck={false}
              maxLength={200}
              enterKeyHint="send"
              aria-label="Terminal input"
            />
          </form>
        </div>
      </div>
    </div>
  );
};

export default Terminal;
